import { useQuery } from '@tanstack/react-query';
import { USE_MOCK, catalogApi } from '@/api/client';
import { MOCK_FUNDAMENTALS_CATALOG, MOCK_BEHAVIORS } from '@/data/mockCatalog';
import type { FundamentalCatalogItem, DailyHabitCatalogItem } from '@/api/types';

/**
 * Variante de `useFundamentals` para Reportería (Fase C/D/E/F/G): incluye Fundamentales/Hábitos
 * inactivos, necesarios para resolver nombres históricos (Distribución por Fundamental, Heatmap,
 * Top Reconocidos con `topFundamentalId`, etc.) aunque el catálogo ya no los muestre hoy.
 *
 * Query key propia (`['catalog', 'fundamentals', 'reporting']`) para no compartir caché con
 * `useFundamentals` — evita colar Fundamentales/Hábitos inactivos al flujo de creación de
 * reconocimientos, donde mostrarlos sería un error de UX.
 *
 * NO usar en flujos de creación/edición: para eso sigue existiendo `useFundamentals`.
 */

/** Palette fallback — used when the backend document has no color field */
const CATALOG_COLOR_PALETTE = [
  '#342F82', '#20B15A', '#F08521', '#E11F26',
  '#40B8E9', '#8B5CF6', '#DC2626', '#EA580C', '#0891B2', '#16A34A',
];

const QUERY_KEY = ['catalog', 'fundamentals', 'reporting'] as const;

export function useFundamentalsForReporting() {
  return useQuery<FundamentalCatalogItem[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (USE_MOCK) {
        return MOCK_FUNDAMENTALS_CATALOG.map((fundamental, idx): FundamentalCatalogItem => ({
          id: fundamental.id,
          code: fundamental.id.slice(0, 3).toUpperCase(),
          order: idx + 1,
          names: { es: fundamental.short, en: fundamental.short },
          dailyHabits: (MOCK_BEHAVIORS[fundamental.id] ?? []).map((habit, habitIdx): DailyHabitCatalogItem => ({
            id: habit.id,
            fundamentalId: fundamental.id,
            code: `${fundamental.id.slice(0, 3).toUpperCase()}-${String(habitIdx + 1).padStart(3, '0')}`,
            order: habitIdx + 1,
            names: { es: habit.name, en: habit.name },
            questionIntro: { es: (habit as { intro?: string }).intro ?? '', en: (habit as { intro?: string }).intro ?? '' },
            isActive: true,
          })),
          isActive: true,
          color: fundamental.color,
          shortName: fundamental.short,
          attitudes: [],
        }));
      }
      const items = await catalogApi.getFundamentalsIncludingInactive();
      return items.map((fundamental, idx) => ({
        ...fundamental,
        color: fundamental.color ?? CATALOG_COLOR_PALETTE[idx % CATALOG_COLOR_PALETTE.length],
        shortName: fundamental.shortName ?? fundamental.names['en'] ?? fundamental.names['es'] ?? fundamental.id,
        attitudes: fundamental.attitudes ?? [],
      }));
    },
    staleTime: 30 * 60 * 1000, // 30 min — catalog rarely changes
  });
}
